export type SequenceType<T> = T extends { id?: number; sequence: number } ? T : never;

// determine the position of the item by it's sequence.
export const indexInSequence = <T>(
  sequenceItem: SequenceType<T>,
  sequence: Array<SequenceType<T>>
): number => {
  return sequence
    .slice()
    .sort((a, b) => a.sequence - b.sequence)
    .map(item => item?.id)
    .indexOf(sequenceItem.id);
};

type ActionFn<T> = (sequenceItem: SequenceType<T>, update: SequenceType<T>) => SequenceType<T>;

export const incrementSequence = <T>(
  sequence: Array<SequenceType<T>>,
  action: ActionFn<T>
): Array<SequenceType<T>> => {
  return sequence.map(sequenceItem => {
    return action(sequenceItem, { ...sequenceItem, sequence: sequenceItem.sequence + 1 });
  });
};

export const decrementSequence = <T>(
  sequence: Array<SequenceType<T>>,
  action: ActionFn<T>
): Array<SequenceType<T>> => {
  return sequence.map(sequenceItem => {
    return action(sequenceItem, { ...sequenceItem, sequence: sequenceItem.sequence - 1 });
  });
};

/**
 * Returns the largest sequence of the set + 1;
 */
export const positionOrHighest = <T>(
  position: number | null | undefined,
  sequence: Array<SequenceType<T>>
): number => {
  let newSequence = position;
  const highest = sequence.reduce((top, item) => {
    return item.sequence > top ? item.sequence : top;
  }, 0);

  if (newSequence === null || newSequence === undefined || newSequence > highest) {
    newSequence = highest + 1;
  }
  return newSequence;
};

export const removeItemFromSequence = <T>(
  sequenceItem: SequenceType<T>,
  sequence: Array<SequenceType<T>>,
  action: ActionFn<T>
): Array<SequenceType<T>> => {
  const before: Array<SequenceType<T>> = [];
  const after: Array<SequenceType<T>> = [];

  sequence.forEach(item => {
    if (item.sequence < sequenceItem.sequence) {
      before.push(item);
    }
    if (item.sequence > sequenceItem.sequence) {
      after.push(item);
    }
  });
  const output = [...before, ...decrementSequence(after, action)];

  return output;
};

export const insertItemInSequence = <T>(
  sequenceItem: SequenceType<T>,
  position: number | null | undefined,
  sequence: Array<SequenceType<T>>,
  action: (item: SequenceType<T>, update: SequenceType<T>) => SequenceType<T>
): {
  sequenceItem: SequenceType<T>;
  list: Array<SequenceType<T>>;
} => {
  const before: Array<SequenceType<T>> = [];
  const after: Array<SequenceType<T>> = [];

  const newSequence = positionOrHighest(position, sequence);

  const workingItem = action(sequenceItem, { ...sequenceItem, sequence: newSequence });

  sequence.forEach(item => {
    if (item.sequence < newSequence) {
      before.push(item);
    } else {
      after.push(item);
    }
  });
  const output = [...before, ...[workingItem], ...incrementSequence(after, action)];
  return { sequenceItem: workingItem, list: output };
};

export const moveItemInSequence = <T>(
  sequenceItem: SequenceType<T>,
  position: number | null | undefined,
  sequence: Array<SequenceType<T>>,
  action: (item: SequenceType<T>, update: SequenceType<T>) => SequenceType<T>
): {
  sequenceItem: SequenceType<T>;
  list: Array<SequenceType<T>>;
  hasChanged: boolean;
} => {
  const before: Array<SequenceType<T>> = [];
  const after: Array<SequenceType<T>> = [];
  const newSequence = positionOrHighest(position, sequence);
  const workingItem = action(sequenceItem, { ...sequenceItem, sequence: newSequence });

  sequence
    .sort((a, b) => a.sequence - b.sequence)
    .forEach(item => {
      // if the item is the same as the workingItem, return, as we'll insert it later
      if (item.id === workingItem.id) {
        return;
      }
      // if the item sequence is the same, but the id is not, we'll need to re-sequence this item
      if (item.sequence === workingItem.sequence) {
        // if the working item is being moved up, put the current item in the before array
        if (workingItem.sequence > sequenceItem.sequence) {
          before.push(item);
          // if the working item is being moved down, put the current item in the after array
        } else if (workingItem.sequence < sequenceItem.sequence) {
          after.push(item);
        }
        return;
      }
      if (item.sequence < workingItem.sequence) {
        before.push(item);
      } else {
        after.push(item);
      }
    });

  const output = before.concat(workingItem, after).map((s, idx) => {
    if (s.id === workingItem.id) {
      workingItem.sequence = idx + 1;
    }

    return {
      ...s,
      sequence: idx + 1,
    };
  });

  // if we're in the same position at the end, don't do anything
  if (indexInSequence(workingItem, output) === indexInSequence(workingItem, sequence)) {
    return { sequenceItem, list: sequence, hasChanged: false };
  }
  return { sequenceItem: workingItem, list: output, hasChanged: true };
};

export const updateSequence = <T>(
  item: SequenceType<T>,
  update: SequenceType<T>
): SequenceType<T> => {
  if (update.sequence !== undefined) {
    return { ...item, ...update };
  }
  return item;
};

export interface ResequencerArgs<T> {
  source: SequenceType<T>;
  position: number;
  items: Array<SequenceType<T>>;
}

export const genericResequencer = <T>(opts: ResequencerArgs<T>) => {
  const sourceItem = opts.source;
  const targetPosition = opts.position;
  const items = opts.items;

  const { sequenceItem, list, hasChanged } = moveItemInSequence<SequenceType<T>>(
    sourceItem,
    targetPosition,
    items,
    updateSequence
  );
  return { sequenceItem, list, hasChanged };
};
